import React from "react";
import { useSeo } from "../lib/useSeo";
import { PublicPageHeader, PublicPageFooter } from "./PublicPageChrome";
import DisclaimerBanner from "./DisclaimerBanner";
import { NC_STATUTES } from "../lib/ncStatutes";

// NCTaxForeclosureGuide — the Tier B public education page for NC tax
// foreclosures. Plain TSX + useSeo + PublicPageHeader/Footer, following
// MecklenburgTaxForeclosures.tsx. It is the long-form companion to the
// per-parcel risk flags: it explains the doctrine the flags are grounded in.
//
// Source of truth: docs/nc-tax-foreclosure/README.md + implementation-guide.md.
// Every legal claim here traces to a statute or case in that index — no
// authority is invented, and no guaranteed/predictive language is used.
//
// It MUST visibly correct the four misconceptions (README §3, §6.4):
//   (a) a tax foreclosure does NOT abolish all other liens;
//   (b) the title-stabilization clock is ONE year (G.S. 105-377), not seven;
//   (c) the "7-year" figure is a borrower-credit / new-mortgage concept,
//       unrelated to tax-deed title insurability;
//   (d) federal tax liens are filed with the CLERK OF SUPERIOR COURT, not the
//       Register of Deeds.

// The statute index renders straight from the shared ncStatutes.ts registry so
// it can never drift from the citations the risk flags use. We render the
// authorities in a stable, readable order rather than object-key order.
const STATUTE_INDEX_ORDER: string[] = [
  "gs_105_355",
  "gs_105_356",
  "gs_105_374",
  "gs_105_375",
  "gs_105_377",
  "gs_1_339_25",
  "gs_1_339_64",
  "gs_44_68_10",
  "gs_153a_140",
  "gs_160a_193",
  "gs_160a_233",
  "gs_160a_443",
  "gs_41_art4",
  "usc_26_6323",
  "usc_26_7425",
  "usc_28_2410d",
];

const CASE_INDEX_ORDER: string[] = [
  "case_dixieland",
  "case_mullane",
  "case_mennonite",
  "case_jones_flowers",
  "case_osteen",
  "case_daye",
  "case_ryan",
  "case_moor",
  "case_tyler_hennepin",
];

const NCTaxForeclosureGuide: React.FC = () => {
  useSeo({
    title: "NC Tax Foreclosure Guide: What Really Survives a Tax Deed | Parcely",
    description:
      "A plain-English guide to North Carolina tax foreclosures for buyers: the two foreclosure methods, what liens actually survive a sale, due process and void sales, the one-year title clock (not seven), the non-warranty deed, title insurance, surplus funds, and a pre-bid checklist.",
  });

  return (
    <>
      <PublicPageHeader />
      <div className="legal-page" id="main" tabIndex={-1}>
        <div className="legal-content">
          <h1>North Carolina Tax Foreclosures: What You're Actually Buying</h1>
          <p className="legal-updated">
            A plain-English guide for buyers and investors — grounded in NC statute and case law
          </p>

          <DisclaimerBanner className="risk-disclaimer--page" />

          <p>
            A tax-foreclosure buyer's core question is simple to ask and easy to get wrong:{" "}
            <strong>what am I actually buying, and what could take it away from me?</strong> This
            guide walks through how North Carolina tax foreclosures work, what survives a sale, the
            ways a sale can be unwound, and the diligence steps that protect you before you bid.
          </p>
          <p>
            It cites the controlling statutes and cases throughout. Anything here is general
            information, not legal advice — and the most common beliefs about tax sales are flatly
            wrong, so read the misconceptions carefully.
          </p>

          {/* 1 — How NC tax foreclosure works (+ two-method table) */}
          <h2>1. How a tax foreclosure works</h2>
          <p>
            When property taxes go unpaid, the county (or a law firm it hires) can force a sale to
            recover what's owed. The tax lien attaches by statute (
            <abbr title="North Carolina General Statutes">G.S.</abbr> 105-355) and has{" "}
            <strong>super-priority</strong> over essentially every other lien on the property
            regardless of recording date (G.S. 105-356) — subordinate only to a senior NCDOR state
            tax lien. North Carolina then allows <strong>two different foreclosure methods</strong>,
            and the choice varies by county and even by parcel. Mecklenburg uses both.
          </p>

          <div className="risk-guide-table-wrap">
            <table className="risk-guide-table">
              <thead>
                <tr>
                  <th>Signal</th>
                  <th>Judicial / mortgage-style (G.S. 105-374)</th>
                  <th>In rem (G.S. 105-375)</th>
                </tr>
              </thead>
              <tbody>
                <tr>
                  <td>Initiating document</td>
                  <td>A complaint — an actual lawsuit</td>
                  <td>A docketed certificate of taxes</td>
                </tr>
                <tr>
                  <td>Who is served</td>
                  <td>Record owner, spouse, other taxing units, and lienholders of record (Rule 4)</td>
                  <td>Taxpayer + lienholders by certified mail; post &amp; publish if mail is not returned</td>
                </tr>
                <tr>
                  <td>Who sells</td>
                  <td>A court-appointed commissioner</td>
                  <td>The sheriff, under execution</td>
                </tr>
                <tr>
                  <td>Cost charged to taxpayer</td>
                  <td>A reasonable attorney fee</td>
                  <td>Capped at $250 (admin fee)</td>
                </tr>
                <tr>
                  <td>Upset-bid statute</td>
                  <td>G.S. 1-339.25 (10 days)</td>
                  <td>G.S. 1-339.64 (10 days)</td>
                </tr>
              </tbody>
            </table>
          </div>
          <p>
            Why it matters to you: the method changes <em>who had to be served</em>, which is the
            hinge on which "this lien is gone" versus "this lien survives" turns (section 4).
          </p>

          {/* 2 — Misconception #1: wipes out all liens */}
          <h2>2. Misconception #1: "A tax foreclosure wipes out all the other liens"</h2>
          <p>
            <strong>This is the single most common — and most expensive — misconception, and it is
            wrong.</strong> Super-priority (G.S. 105-356) means the tax lien is paid first and can't
            itself be wiped out by a private foreclosure. It does <strong>not</strong> mean the sale
            automatically vaporizes every other lien.
          </p>
          <p>
            A junior lien is extinguished only through the ordinary mechanism of a senior
            foreclosure cutting off juniors —{" "}
            <em>Dixieland Realty Co. v. Wysor</em>, 272 N.C. 172 (1967) — and{" "}
            <strong>only if that junior lienholder was both joined as a party AND properly served.</strong>{" "}
            A lienholder who was never brought into the action keeps its lien. Priority extinguishes
            nothing without service. So a "clean" tax-sale parcel can still carry a mortgage, a
            judgment lien, or a government lien that nobody told you about.
          </p>

          {/* 3 — What survives (incl. IRS 120-day redemption + nuisance vs demo) */}
          <h2>3. What actually survives a clean sale</h2>
          <p>
            Even a textbook-clean sale conveys the property <strong>subject to</strong> several
            things. A defective sale leaves even more alive (section 5). The survivors:
          </p>
          <ul>
            <li>
              <strong>Subsequent-year taxes</strong> and taxes whose amount couldn't be determined
              at judgment time — the sale is free and clear only of interests <em>included</em> in
              the action (G.S. 105-374 / G.S. 105-375). Your true cost is the bid plus these.
            </li>
            <li>
              <strong>Federal (IRS) tax liens</strong> — the highest-stakes case. A federal lien
              survives if the IRS wasn't properly noticed under <strong>26 U.S.C. § 7425</strong>{" "}
              (written notice at least 25 days before the sale). And <strong>even when notice is
              correct, the IRS keeps a 120-day right of redemption</strong> after the sale (
              <strong>28 U.S.C. § 2410(d)</strong>) — your title isn't fully quiet until that window
              closes. The local tax lien is senior under § 6323(b)(6), but seniority does not cure
              the notice rule or the redemption right.
            </li>
            <li>
              <strong>Nuisance-abatement liens</strong> (weed/trash) — G.S. 153A-140 (county) /
              G.S. 160A-193 (city) — carry the <em>same super-priority as property taxes</em> and
              survive if the abating government wasn't joined.
            </li>
            <li>
              <strong>Demolition / minimum-housing liens</strong> — G.S. 160A-443 — sit at{" "}
              <em>special-assessment priority (junior to the tax lien)</em>, but still land on the
              new owner if the city wasn't joined. These often dwarf the tax: a $1,200 tax bill can
              sit under a $15,000 demolition lien. <strong>Nuisance and demolition liens are not the
              same priority</strong> — that distinction governs the payout order, but for your "what
              do I still owe" question, either can ride through.
            </li>
            <li>
              <strong>Easements, restrictive covenants, and HOA declarations</strong> generally
              survive as servitudes running with the land. (An HOA's unpaid-<em>assessment</em> lien
              is different — it behaves like a junior financial lien and dies only if the HOA was
              joined and served.)
            </li>
          </ul>

          {/* 4 — Due process */}
          <h2>4. Due process: the most common reason sales get unwound</h2>
          <p>
            Defective service is the leading reason NC tax-foreclosure sales are challenged and
            reversed. The standard comes from a line of cases:
          </p>
          <ul>
            <li>
              <em>Mullane v. Central Hanover Bank &amp; Trust</em> (1950): notice must be reasonably
              calculated to apprise interested parties.
            </li>
            <li>
              <em>Mennonite Board of Missions v. Adams</em>, 462 U.S. 791 (1983): a recorded
              mortgagee is entitled to actual mailed or personal notice — publication alone is not
              enough for a party whose identity and address are ascertainable.
            </li>
            <li>
              <em>Jones v. Flowers</em>, 547 U.S. 220 (2006): when mailed notice comes back
              undelivered, the government must take additional reasonable steps before selling.
            </li>
            <li>
              <em>Mecklenburg County v. Ryan</em>, 281 N.C. App. 646 (2022): the county had the
              owner's email but used publication anyway — held insufficient; the owner had in fact
              redeemed by phone.
            </li>
          </ul>
          <p>
            When a known or reasonably identifiable owner or lienholder isn't properly served, the
            judgment is <strong>void for lack of personal jurisdiction</strong> as to that party,
            that party's interest <strong>survives</strong>, and — critically — the void judgment can
            be attacked <strong>at any time</strong> under Rule 60(b)(4). That's what defeats the
            one-year bar discussed next.
          </p>

          {/* 5 — The challenge clock + 7-year myth */}
          <h2>5. The challenge clock — and the 7-year myth</h2>
          <p>
            <strong>G.S. 105-377</strong> bars contests to title <strong>one year</strong> after the
            deed is recorded. That clock stabilizes title against ordinary defects — but it does{" "}
            <strong>not</strong> run against a judgment that is void for defective notice (
            <em>Henderson County v. Osteen</em>, 292 N.C. 692 (1977)).
          </p>
          <p className="risk-guide-callout">
            <strong>Misconception #2 and #3 — debunked.</strong> You will hear "you're safe after
            seven years." That is <strong>false and irrelevant here</strong>. The seven-year figure
            is a <strong>borrower-credit / new-mortgage concept</strong> — how long a foreclosure
            stays on a <em>borrower's</em> credit report, or the seasoning period before a borrower
            qualifies for a new conventional loan. It has <strong>nothing</strong> to do with
            tax-deed title insurability. The controlling title figure is <strong>one year</strong>{" "}
            (G.S. 105-377), subject to the void-judgment exception — <strong>not seven</strong>.
          </p>
          <p>
            And "confirmation means it's mine, period" is incomplete: confirmation closes redemption
            and upset bids, but the defective-notice attack survives both confirmation and the
            one-year bar.
          </p>

          {/* 6 — The non-warranty deed */}
          <h2>6. The deed you receive is a non-warranty deed</h2>
          <p>
            A tax-foreclosure buyer receives a <strong>non-warranty commissioner's deed</strong>{" "}
            (judicial) or sheriff's deed (in rem) — "as-is, buyer-beware," with no warranty of title.
            Liens that were extinguished by operation of law typically stay in the public record with
            no recorded release, because nobody files a satisfaction. The result is a <em>cloud</em>{" "}
            on title: the record still shows liens that are legally dead (if service was good) or
            legally alive (if it wasn't).
          </p>

          {/* 7 — Title insurance reality */}
          <h2>7. Title insurance: the reality</h2>
          <p>
            Most buyers need insurable title to resell or finance. Title insurers typically require
            one of two cures before they'll insure a tax deed:
          </p>
          <ul>
            <li>
              <strong>A quiet-title action</strong> — Chapter 41, Article 4 (historically G.S.
              41-10). Ballpark <strong>$5,000–$6,000 and several months</strong> (an estimate —
              every case differs).
            </li>
            <li>
              <strong>A non-judicial tax-title certification</strong> — a Tax Title Services-type
              third-party review, roughly <strong>30–40 days</strong>, where the underwriter accepts
              the certification. Cheaper and faster than litigation.
            </li>
          </ul>
          <p>
            NC closings require an attorney's title opinion — there is no DIY path to insurable
            title. None of these figures are guaranteed; they are typical ranges to budget against.
          </p>

          {/* 8 — Tyler v. Hennepin / surplus */}
          <h2>8. Surplus funds (for former owners)</h2>
          <p>
            If you are a <em>former</em> owner rather than a buyer: in{" "}
            <em>Tyler v. Hennepin County</em> (2023) the U.S. Supreme Court held that a foreclosing
            government generally may not keep surplus equity beyond what it is actually owed. If a
            sale brings more than the debt, that surplus is a distinct question from buyer risk —
            and worth asking an attorney about.
          </p>

          {/* 9 — Protect-yourself checklist */}
          <h2>9. Protect yourself before you bid</h2>
          <ul>
            <li>
              <strong>Search for federal tax liens at the Clerk of Superior Court — NOT the Register
              of Deeds.</strong> Under the Uniform Federal Lien Registration Act (G.S. 44-68.10 et
              seq.), federal tax liens on real property are filed with the{" "}
              <strong>Clerk of Superior Court</strong>; entity liens are also searchable at the NC
              Secretary of State. <strong>A clean Register-of-Deeds search is not a clean federal-lien
              search</strong> — this is misconception #4, and it's a frequent, costly diligence miss.
            </li>
            <li>
              <strong>Identify the foreclosure method</strong> (judicial vs in rem) from the case
              record — it tells you who had to be served.
            </li>
            <li>
              <strong>Read the party list and the affidavits.</strong> Look for "Unknown Heirs of …"
              designations and publication-only service of a named, identifiable party — the
              conditions that predict a void sale.
            </li>
            <li>
              <strong>Budget for subsequent-year taxes</strong> and any surviving government liens —
              your real cost is bid + survivors, not the bid.
            </li>
            <li>
              <strong>Confirm a title-insurance path</strong> (quiet title or certification) before
              you bid, not after.
            </li>
            <li>
              <strong>Have an NC-licensed attorney review the chain of title.</strong> A title
              opinion is required to close, and it's the backstop for everything above.
            </li>
          </ul>

          {/* 10 — Glossary / statute index */}
          <h2>10. Statute &amp; case index</h2>
          <p>
            Every legal point above traces to one of these authorities. Where an authority is
            uncertain, that is noted verbatim — confirm it before relying on it.
          </p>

          <h3 className="risk-guide-subhead">North Carolina &amp; federal statutes</h3>
          <ul className="risk-guide-cites">
            {STATUTE_INDEX_ORDER.map((key) => {
              const c = NC_STATUTES[key];
              if (!c) return null;
              return (
                <li key={key}>
                  {c.url ? (
                    <a href={c.url} target="_blank" rel="noopener noreferrer">
                      <strong>{c.label}</strong>
                    </a>
                  ) : (
                    <strong>{c.label}</strong>
                  )}{" "}
                  — {c.summary}
                </li>
              );
            })}
          </ul>

          <h3 className="risk-guide-subhead">Cases</h3>
          <ul className="risk-guide-cites">
            {CASE_INDEX_ORDER.map((key) => {
              const c = NC_STATUTES[key];
              if (!c) return null;
              return (
                <li key={key}>
                  <strong>{c.citation}</strong> — {c.summary}
                </li>
              );
            })}
          </ul>

          <h2>How Parcely helps</h2>
          <p>
            Parcely surfaces these risks per parcel — the foreclosure method, surviving-lien signals,
            title-insurability notes, and explicit "we haven't checked this yet" flags — so the
            unknowns are visible <em>before</em> you bid instead of after. Every flag carries the
            statute or case it's grounded in.
          </p>
          <p>
            <a
              href="/public/mecklenburg-nc"
              className="btn-primary"
              style={{ display: "inline-block", textDecoration: "none", marginTop: "0.5rem" }}
            >
              See NC foreclosure data →
            </a>
          </p>

          <hr className="legal-divider" />
          <p>
            <strong>This is general information, not legal, financial, tax, or title advice.</strong>{" "}
            Parcely is an information tool, not a title search or a title opinion. Tax-foreclosure
            rules, deposit amounts, deadlines, and case citations change, and every parcel has its
            own facts. Verify everything — including the authorities above — with the county of
            record, the Clerk of Superior Court, and an NC-licensed attorney before you bid.
          </p>
          <p className="legal-entity">
            <strong>Parcely LLC</strong>
            <br />
            Charlotte, NC
          </p>
        </div>
      </div>
      <PublicPageFooter />
    </>
  );
};

export default NCTaxForeclosureGuide;
